<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col>
          <b-button variant="primary" @click="openAddModal">+ Add Contract</b-button>
        </b-col>
      </b-row>

      <!-- Loading skeleton -->
      <div v-if="loading">
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </div>

      <div v-else-if="!items.length" class="text-center py-4 text-muted">
        No contracts found for this integrator.
      </div>

      <b-table
        v-else
        :items="items"
        :fields="fields"
        striped
        hover
        responsive
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
        </template>
        <template #cell(chick_rate)="data">₹{{ data.value }}</template>
        <template #cell(feed_rate)="data">₹{{ data.value }}</template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-success" class="mr-1" :to="`/integrator-contracts/${data.item.id}/settlements`">
            Settlements
          </b-button>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditModal(data.item)">Edit</b-button>
          <b-button size="sm" variant="outline-danger" @click="deleteItem(data.item.id)">Delete</b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="editItem ? 'Edit Contract' : 'Add Contract'"
      ok-title="Save"
      @ok="saveItem"
      @hidden="resetForm"
    >
      <b-form-group label="Farm">
        <b-form-select v-model="form.farm_id" :options="farmOptions" />
      </b-form-group>
      <b-form-group label="Flock ID (optional)">
        <b-form-input v-model="form.flock_id" placeholder="Flock ID" />
      </b-form-group>
      <b-form-group label="Contract Type">
        <b-form-select v-model="form.contract_type" :options="contractTypeOptions" />
      </b-form-group>
      <b-form-group label="Contract Date">
        <b-form-input v-model="form.contract_date" type="date" />
      </b-form-group>
      <b-form-group label="Chick Rate (₹)">
        <b-form-input v-model="form.chick_rate" type="number" step="0.01" />
      </b-form-group>
      <b-form-group label="Feed Rate (₹/kg)">
        <b-form-input v-model="form.feed_rate" type="number" step="0.01" />
      </b-form-group>
      <b-form-group label="Target FCR">
        <b-form-input v-model="form.target_fcr" type="number" step="0.01" />
      </b-form-group>
      <b-form-group label="Status">
        <b-form-select v-model="form.status" :options="statusOptions" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner, BModal,
  BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue'

export default {
  name: 'IntegratorContracts',
  components: {
    BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner, BModal,
    BFormGroup, BFormInput, BFormSelect,
  },
  data() {
    return {
      loading: false,
      items: [],
      showModal: false,
      editItem: null,
      farms: [],
      form: {
        farm_id: '',
        flock_id: '',
        contract_type: 'full',
        contract_date: '',
        chick_rate: '',
        feed_rate: '',
        target_fcr: '',
        status: 'active',
      },
      fields: [
        { key: 'flock_id', label: 'Flock' },
        { key: 'contract_type', label: 'Contract Type' },
        { key: 'chick_rate', label: 'Chick Rate' },
        { key: 'feed_rate', label: 'Feed Rate' },
        { key: 'target_fcr', label: 'Target FCR' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'settled', text: 'Settled' },
        { value: 'cancelled', text: 'Cancelled' },
      ],
      contractTypeOptions: [
        { value: 'full', text: 'Full' },
        { value: 'partial', text: 'Partial' },
      ],
    }
  },
  computed: {
    farmOptions() {
      return [
        { value: '', text: '— Select Farm —' },
        ...this.farms.map(f => ({ value: f.id, text: f.name || f.farm_name || `Farm #${f.id}` })),
      ]
    },
  },
  created() {
    this.fetchFarms()
    this.fetchData()
  },
  methods: {
    async fetchFarms() {
      try {
        const { data } = await axios.get('web/getFarmList')
        this.farms = data.farm_list || data.data?.data || data.data || data.items || []
      } catch (e) {
        console.error('Failed to load farms', e)
      }
    },
    async fetchData() {
      const { integratorId } = this.$route.params
      this.loading = true
      try {
        const { data } = await axios.get(`web/integrators/${integratorId}/contracts`)
        // Returns { data: paginator } → paginator.data = items
        this.items = data.data?.data || data.data || data.items || data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load contracts', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editItem = null
      this.resetForm()
      this.showModal = true
    },
    openEditModal(item) {
      this.editItem = item
      this.form = {
        ...item,
        contract_date: item.contract_date ? item.contract_date.toString().substring(0, 10) : '',
      }
      this.showModal = true
    },
    resetForm() {
      this.form = { farm_id: '', flock_id: '', contract_type: 'full', contract_date: '', chick_rate: '', feed_rate: '', target_fcr: '', status: 'active' }
      this.editItem = null
    },
    async saveItem(bvModalEvt) {
      bvModalEvt.preventDefault()
      const { integratorId } = this.$route.params
      try {
        if (this.editItem) {
          await axios.put(`web/integrators/${integratorId}/contracts/${this.editItem.id}`, this.form)
          this.$bvToast.toast('Contract updated', { variant: 'success', title: 'Success' })
        } else {
          await axios.post(`web/integrators/${integratorId}/contracts`, this.form)
          this.$bvToast.toast('Contract added', { variant: 'success', title: 'Success' })
        }
        this.showModal = false
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to save contract', { variant: 'danger', title: 'Error' })
      }
    },
    async deleteItem(id) {
      if (!confirm('Delete this contract?')) return
      const { integratorId } = this.$route.params
      try {
        await axios.delete(`web/integrators/${integratorId}/contracts/${id}`)
        this.$bvToast.toast('Deleted', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to delete', { variant: 'danger', title: 'Error' })
      }
    },
    statusVariant(status) {
      const map = { active: 'success', completed: 'primary', cancelled: 'danger' }
      return map[status] || 'secondary'
    },
  },
}
</script>

<style scoped></style>
